import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogComponent } from '@syncfusion/ej2-react-popups';
import {
  DiagramComponent,
  Inject,
  DataBinding,
  FlowchartLayout,
  DiagramConstraints,
} from '@syncfusion/ej2-react-diagrams';
import { ProductionOrderControllerService } from '../api';
import type { ProductionOrderDto } from '../api/models/ProductionOrderDto';
import type { ProductionOperationDto } from '../api/models/ProductionOperationDto';
import type { ProductionOperationRelationshipDto } from '../api/models/ProductionOperationRelationshipDto';

interface ProductionOrderWithId extends ProductionOrderDto {
  id: string;
}

interface ProductionOrderVisualizationDialogProps {
  productionOrder: ProductionOrderWithId | null;
  visible: boolean;
  onClose: () => void;
}

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds == null || seconds <= 0) return '–';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}min` : `${m}min ${s}s`;
};

const formatQuantity = (q: { quantity?: number; unit?: string }): string => {
  const n = q.quantity;
  if (n == null) return '–';
  const u = (q.unit || '').trim();
  return u ? `${n} ${u}` : String(n);
};

const createOperationNode = (op: ProductionOperationDto): any => {
  const annotations: any[] = [
    {
      id: 'name',
      content: op.name || 'Unbenannt',
      style: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#000',
      },
      offset: { x: 0.5, y: 0.15 },
    },
  ];
  let y = 0.38;
  if (op.position != null) {
    annotations.push({
      id: 'position',
      content: `#${op.position}`,
      style: { fontSize: 10, color: '#666' },
      offset: { x: 0.5, y },
    });
    y += 0.15;
  }
  const durationStr = formatDuration(op.durationSeconds);
  annotations.push({
    id: 'duration',
    content: `Dauer: ${durationStr}`,
    style: { fontSize: 10, color: '#444' },
    offset: { x: 0.5, y },
  });
  y += 0.15;
  const quantities = op.inputQuantities;
  if (quantities && quantities.length > 0) {
    const quantityLines = quantities.map((iq) => formatQuantity(iq)).join(', ');
    annotations.push({
      id: 'quantities',
      content: quantityLines.length > 20 ? quantityLines.slice(0, 18) + '…' : quantityLines,
      style: { fontSize: 10, color: '#444' },
      offset: { x: 0.5, y },
    });
    y += 0.15;
  }
  const id = op.uuid || `op_${op.position ?? 0}_${Date.now()}`;
  const nodeHeight = Math.max(80, 50 + annotations.length * 22);
  return {
    id,
    shape: {
      type: 'Basic',
      shape: 'Rectangle',
      cornerRadius: 5,
    },
    width: 200,
    height: nodeHeight,
    style: {
      fill: '#e8f4fc',
      strokeColor: '#333',
      strokeWidth: 2,
    },
    annotations,
  };
};

const formatDistance = (seconds: number | null | undefined): string => {
  if (seconds == null) return '–';
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return s === 0 ? `${m}min` : `${m}min ${s}s`;
};

const createConnectorFromRelationship = (rel: ProductionOperationRelationshipDto, index: number): any => {
  const sourceId = rel.predecessorUuid || '';
  const targetId = rel.successorUuid || '';
  const minStr = formatDistance(rel.minDistanceSeconds);
  const maxStr = formatDistance(rel.maxDistanceSeconds);
  const hasMin = rel.minDistanceSeconds != null;
  const hasMax = rel.maxDistanceSeconds != null;
  const labelParts: string[] = [];
  if (hasMin) labelParts.push(`Min: ${minStr}`);
  if (hasMax) labelParts.push(`Max: ${maxStr}`);
  const label = labelParts.length > 0 ? labelParts.join(', ') : null;

  const connector: any = {
    id: rel.uuid ? `conn_${rel.uuid}` : `conn_${index}_${sourceId}_${targetId}`,
    sourceID: sourceId,
    targetID: targetId,
    type: 'Orthogonal',
    cornerRadius: 8,
    connectorSpacing: 16,
    style: {
      strokeColor: '#555',
      strokeWidth: 1.5,
    },
  };
  if (label) {
    connector.annotations = [
      {
        content: label,
        offset: 0.5,
        style: {
          fontSize: 12,
          color: '#111',
          fontWeight: '600',
          fill: '#fff',
          strokeColor: '#555',
          strokeWidth: 1,
          textAlign: 'Center',
        },
      },
    ];
  }
  return connector;
};

const ProductionOrderVisualizationDialog: React.FC<ProductionOrderVisualizationDialogProps> = ({
  productionOrder,
  visible,
  onClose,
}) => {
  const { t } = useTranslation(['common']);
  const [operations, setOperations] = useState<ProductionOperationDto[]>([]);
  const [relationships, setRelationships] = useState<ProductionOperationRelationshipDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible || !productionOrder?.uuid) {
      setOperations([]);
      setRelationships([]);
      return;
    }
    setLoading(true);
    const orderUuid = productionOrder.uuid;
    Promise.all([
      ProductionOrderControllerService.getProductionOperations(orderUuid),
      ProductionOrderControllerService.getProductionOperationRelationships(orderUuid),
    ])
      .then(([opsList, relsList]) => {
        setOperations(Array.isArray(opsList) ? opsList : []);
        setRelationships(Array.isArray(relsList) ? relsList : []);
      })
      .catch(() => {
        setOperations([]);
        setRelationships([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [visible, productionOrder?.uuid]);

  const operationIds = useMemo(() => new Set(operations.map((op) => op.uuid).filter(Boolean)), [operations]);

  const diagramData = useMemo(() => {
    if (!operations.length) return { nodes: [], connectors: [] };
    const sorted = [...operations].sort(
      (a, b) => (a.position ?? 0) - (b.position ?? 0)
    );
    const nodes = sorted.map(createOperationNode);
    const connectors: any[] = relationships
      .filter(
        (rel) =>
          rel.relationshipCategory === 'TIME' &&
          rel.predecessorUuid &&
          rel.successorUuid &&
          operationIds.has(rel.predecessorUuid) &&
          operationIds.has(rel.successorUuid)
      )
      .map((rel, index) => createConnectorFromRelationship(rel, index));
    return { nodes, connectors };
  }, [operations, relationships, operationIds]);

  const title = productionOrder
    ? `${t('productionOrder.visualization.title', 'Struktur')}: ${productionOrder.orderNumber || productionOrder.uuid}`
    : t('productionOrder.visualization.title', 'Struktur');

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const closeLabel = t('actions.close', 'Schließen');
  const dialogButtons = useMemo(
    () => [
      { buttonModel: { content: closeLabel, isPrimary: true }, click: handleClose },
    ],
    [handleClose, closeLabel]
  );

  return (
    <>
      <style>
        {`
          .production-order-visualization-dialog .e-dlg-content {
            padding: 0 !important;
          }
          .production-order-visualization-dialog .e-dlg-content > div {
            padding: 0 !important;
          }
        `}
      </style>
      <DialogComponent
        width="90vw"
        height="95vh"
        visible={visible}
        close={onClose}
        showCloseIcon
        isModal
        cssClass="production-order-visualization-dialog"
        header={title}
        buttons={dialogButtons}
      >
        {productionOrder && (
          <div className="production-order-visualization-header flex-shrink-0 text-sm text-gray-600 border-b border-gray-200 pb-2 pt-1 px-4">
            {productionOrder.routingName && (
              <span className="mr-4">
                {t('productionOrder.grid.field.routingName', 'Arbeitsplan')}: {productionOrder.routingName}
              </span>
            )}
            {productionOrder.campaignId && (
              <span className="mr-4">
                {t('productionOrder.grid.field.campaignId', 'Kampagne')}: {productionOrder.campaignId}
              </span>
            )}
            {productionOrder.materialName && (
              <span>
                {t('productionOrder.grid.field.materialName', 'Material')}: {productionOrder.materialName}
              </span>
            )}
          </div>
        )}
        <div className="p-3">
          {loading ? (
            <div className="flex items-center justify-center min-h-[200px]">
              <p className="text-gray-500">{t('common.loading', 'Laden…')}</p>
            </div>
          ) : diagramData.nodes.length > 0 ? (
            <div style={{ width: '100%', height: '500px', minWidth: '700px' }}>
              <DiagramComponent
                key={`po-diagram-${productionOrder?.uuid ?? ''}-${diagramData.nodes.length}`}
                id="production-order-diagram"
                width="100%"
                height="500px"
                nodes={diagramData.nodes}
                connectors={diagramData.connectors}
                layout={{
                  type: 'Flowchart',
                  orientation: 'TopToBottom',
                  horizontalSpacing: 100,
                  verticalSpacing: 80,
                  margin: { left: 60, top: 60, right: 60, bottom: 60 },
                  flowchartLayoutSettings: {},
                }}
                constraints={DiagramConstraints.Default}
                scrollSettings={{
                  scrollLimit: 'Diagram',
                  canAutoScroll: true,
                }}
                snapSettings={{
                  constraints: 0,
                }}
                tool={0}
                selectedItems={{
                  constraints: undefined,
                }}
              >
                <Inject services={[DataBinding, FlowchartLayout]} />
              </DiagramComponent>
            </div>
          ) : (
            <div className="flex items-center justify-center min-h-[120px]">
              <p className="text-gray-500">
                {productionOrder
                  ? t('productionOrder.visualization.noOperations', 'Keine Operationen vorhanden')
                  : t('productionOrder.visualization.noOrder', 'Kein Produktionsauftrag ausgewählt')}
              </p>
            </div>
          )}
        </div>
      </DialogComponent>
    </>
  );
};

export default ProductionOrderVisualizationDialog;
